/**
 * Platform Admin Layout
 * Completely separate from tenant app UI.
 * Uses its own sidebar and header, dark theme consistent with Rapid X.
 */
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard,
  Building2,
  BarChart3,
  ScrollText,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronRight,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/platform', icon: Building2, label: 'Tenants', exact: true },
  { to: '/platform/metrics', icon: BarChart3, label: 'Metrics' },
  { to: '/platform/alerts', icon: AlertTriangle, label: 'Alerts' },
  { to: '/platform/audit', icon: ScrollText, label: 'Audit Log' },
];

export default function PlatformLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBackToApp = () => {
    navigate('/dashboard');
  };

  return (
    <div data-testid="platform-layout" className="min-h-screen bg-[#07070a] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-60 bg-[#0c0c10] border-r border-amber-900/20 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-amber-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="text-sm font-bold text-white tracking-wide">RAPID X</p>
                  <p className="text-[10px] text-amber-500/80 font-mono uppercase tracking-widest">Platform Admin</p>
                </div>
              </div>
              <button
                className="lg:hidden text-zinc-400 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                    isActive
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`
                }
                data-testid={`platform-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-amber-900/20 space-y-2">
            <button
              onClick={handleBackToApp}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-colors"
              data-testid="platform-back-to-app"
            >
              <ChevronRight className="w-3 h-3 rotate-180" />
              Open Tenant Application
            </button>
            <div className="px-3 py-2">
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              <p className="text-[10px] text-amber-500/60 font-mono">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/5 transition-colors"
              data-testid="platform-logout"
            >
              <LogOut className="w-3 h-3" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-12 bg-[#0c0c10]/95 backdrop-blur-sm border-b border-amber-900/20 flex items-center justify-between px-4 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-zinc-400 hover:text-white p-1"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <span className="text-[10px] font-mono text-amber-500/60 uppercase tracking-widest">
              Platform Administration
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
